import React, { useMemo, useRef } from 'react'
import { FlagsProvider } from 'react-feature-flags'
import { FeatureFlagServicePromiseClient } from '@trustero/trustero-api-web/lib/featureflag/featureflag_grpc_web_pb'
import {
  ListFeatureFlagOverridesRequest,
  ListFeatureFlagOverridesResponse,
} from '@trustero/trustero-api-web/lib/featureflag/featureflag_pb'
import { useSwrImmutableGrpc } from 'src/components/async/useSwrImmutableGrpc'
import { NewGrpcResponse } from 'src/components/async/hooks/types'
import {
  FlagOverride,
  GatekeeperFlag,
  IsGatekeeperEnabled,
  setFeatureFlagOverrides,
} from './Gatekeeper'

/**
 * Loads this environment's feature flag overrides and republishes every flag's
 * resolved value to its subtree.
 *
 * Mounted inside the auth stack, unlike GatekeeperProvider, which sits above
 * auth and resolves flags from config.json alone. The two form a fallback
 * chain: the outer provider guarantees every flag has a value before anything
 * renders, and this one upgrades those values once a session exists.
 *
 * Before a token exists, the RPC hook skips the request and children render
 * with config defaults. Once authenticated, it waits for the first fetch to
 * settle before mounting children. After that it always renders: losing the
 * cached response is not a reason to erase the app. A failed first request
 * leaves flags at their config defaults.
 */
export const FeatureFlagProvider = ({
  children,
}: {
  children: JSX.Element
}): JSX.Element | null => {
  const request = useMemo(() => new ListFeatureFlagOverridesRequest(), [])
  const { response } = useSwrImmutableGrpc(
    FeatureFlagServicePromiseClient.prototype.listFeatureFlagOverrides,
    request,
  )
  const { data, loading } =
    NewGrpcResponse<ListFeatureFlagOverridesResponse>(response)
  const canFetch = response.access === 'allowed'

  // Written during render rather than in an effect: the 300+ synchronous
  // IsGatekeeperEnabled callers below read the module store directly, so it has
  // to be populated before children mount, not after.
  const flags = useMemo(() => {
    if (data) {
      const overrides: Record<string, FlagOverride> = {}
      data.getOverridesList().forEach((override) => {
        overrides[override.getKey()] = {
          enabled: override.getEnabled(),
          minAppVersion: override.getMinAppVersion(),
        }
      })
      setFeatureFlagOverrides(overrides)
    }
    return Object.values(GatekeeperFlag).map((flag) => ({
      name: flag,
      isActive: IsGatekeeperEnabled(flag),
    }))
  }, [data])

  // Blocks until the request FIRST settles -- success or failure -- and never
  // again. Settled, not succeeded: a failed first request leaves every flag at
  // its config default, which is a complete answer and the behaviour that
  // predates this provider. Keying on `data` instead would leave a session that
  // started with a failed fetch still able to blank, by the same route.
  //
  // It could, and did: DataModelAdapter.reload clears the WHOLE SWR cache
  // (cache.clear()), which takes this entry with it, and the hook then reports
  // data: undefined -- indistinguishable from in-flight. Returning null there
  // erases every element in the app at once, with no error for a boundary to
  // catch and nothing in the console. Nothing brings it back either:
  // useSwrImmutableGrpc disables revalidateOnFocus and revalidateOnReconnect,
  // so the page stays blank until a manual reload.
  //
  // Written during render alongside the store above, for the same reason: the
  // decision is needed on this render, not after an effect.
  const hasSettled = useRef(false)
  // A request skipped before sign-in has not settled yet.
  if (canFetch && !loading) {
    hasSettled.current = true
  }

  if (canFetch && !hasSettled.current) {
    return null
  }

  return <FlagsProvider value={flags}>{children}</FlagsProvider>
}
